<template>
  <div class="h-full kendo-wrapper-height-adjust">
    <div class="loader" v-if="loading">
      <loading :opacity="0" :active.sync="loading" color="var(--primary-DEFAULT)"></loading>
    </div>
    <localization :language="$i18n.locale">
      <intl :class="intlClass" :locale="$i18n.locale">
        <Grid
          id="grid"
          ref="grid"
          :class="gridClass"
          cellRender="common"
          headerCellRender="headerTemplate"
          :detail="detailTemplate"
          :edit-field="inEditField"
          :expand-field="expandField"
          :selected-field="selectedField"
          :data-items="kendo.dataResult"
          :columns="columns"
          :reorderable="true"
          :resizable="true"
          :sortable="kendo.sortable"
          :filterable="kendo.filterable"
          :groupable="groupable"
          :skip="kendo.skip"
          :take="kendo.take"
          :sort="kendo.sort"
          :group="kendo.group"
          :filter="kendo.filter"
          :selectable="true"
          @cellclick="cellclick"
          @rowclick="rowclick"
          @itemchange="itemChange"
          @datastatechange="datastatechange"
          @columnreorder="columnreorder"
          @selectionchange="selectionchange"
          @sortchange="sortchange"
          @headerselectionchange="headerselectionchange"
          @expandchange="expandchange"
          @columnresize="columnResize"
          @scroll="scroll">
          <template v-slot:common="{ props, listeners }">
            <CommonTemplate :set="col = getColumn(props)"
              :table-id="_uid"
              :field="props.field"
              :data-item="props.dataItem"
              :format="props.format || col ? col.format : undefined"
              :class-name="props.className"
              :column-index="props.columnIndex"
              :columns-count="props.columnsCount"
              :row-type="props.rowType"
              :level="props.level"
              :expanded="props.expanded"
              :editor="props.editor"
              :type="props.type"
              :cur-edit-field="editField"
              :edit-type="kendo.editable"
              :edit-field="inEditField"
              :edit-detail-field="inEditDetailField"
              :extraparams="getExtra(props, col)"
              :cell="col ? col.cell : undefined"
              v-on="listeners"
              @register-template="registerTemplateRef"
              @unregister-template="unregisterTemplateRef"
            ></CommonTemplate>
          </template>
          <template v-slot:selectionTemplate="{ props, listeners }">
            <SelectionTemplate
              :table-id="_uid"
              :field="props.field"
              :data-item="props.dataItem"
              :format="props.format"
              :class-name="props.className"
              :column-index="props.columnIndex"
              :columns-count="props.columnsCount"
              :row-type="props.rowType"
              :level="props.level"
              :expanded="props.expanded"
              :editor="props.editor"
              :type="props.type"
              :extraparams="getExtra(props)"
              :single="single"
              :selected-field="selectedField"
              v-on="listeners"
              @register-template="registerTemplateRef"
              @unregister-template="unregisterTemplateRef"
            ></SelectionTemplate>
          </template>
          <template v-slot:expandTemplate="{ props, listeners }">
            <ExpandTemplate
              :table-id="_uid"
              :field="props.field"
              :data-item="props.dataItem"
              :format="props.format"
              :class-name="props.className"
              :column-index="props.columnIndex"
              :columns-count="props.columnsCount"
              :row-type="props.rowType"
              :level="props.level"
              :expanded="props.expanded"
              :editor="props.editor"
              :type="props.type"
              :extraparams="getExtra(props)"
              v-on="listeners"
              @register-template="registerTemplateRef"
              @unregister-template="unregisterTemplateRef"
            ></ExpandTemplate>
          </template>
          <template v-slot:myButtomTemplate="{ props }">
            <ActionCell v-if="props.dataItem && !props.dataItem.aggregates" :class="props.className" :data-item="props.dataItem" @actionclick="actionClicked" />
          </template>
          <template v-slot:actionsTemplate="{ props, listeners }">
            <ActionsTemplate v-if="props.dataItem && !props.dataItem.aggregates"
              :table-id="_uid"
              :field="props.field"
              :data-item="props.dataItem"
              :format="props.format"
              :class-name="props.className"
              :column-index="props.columnIndex"
              :columns-count="props.columnsCount"
              :row-type="props.rowType"
              :level="props.level"
              :expanded="props.expanded"
              :editor="props.editor"
              :type="props.type"
              :cur-edit-field="editField"
              :edit-type="kendo.editable"
              :edit-field="inEditField"
              :edit-detail-field="inEditDetailField"
              :extraparams="getExtra(props)"
              @actionclick="actionClicked"
              v-on="listeners"
              @register-template="registerTemplateRef">
              @unregister-template="unregisterTemplateRef">
            </ActionsTemplate>
          </template>
          <template v-slot:fechaTemplate="{ props, listeners }">
            <FechaTemplate
              :table-id="_uid"
              :field="props.field"
              :data-item="props.dataItem"
              :format="props.format"
              :class-name="props.className"
              :column-index="props.columnIndex"
              :columns-count="props.columnsCount"
              :row-type="props.rowType"
              :level="props.level"
              :expanded="props.expanded"
              :editor="props.editor"
              :type="props.type"
              :cur-edit-field="editField"
              :edit-type="kendo.editable"
              :edit-field="inEditField"
              :edit-detail-field="inEditDetailField"
              :extraparams="getExtra(props)"
              v-on="listeners"
              @register-template="registerTemplateRef"
              @unregister-template="unregisterTemplateRef"
            ></FechaTemplate>
          </template>
          <template v-slot:numberTemplate="{ props, listeners }">
            <NumberTemplate
              :table-id="_uid"
              :field="props.field"
              :data-item="props.dataItem"
              :format="props.format"
              :class-name="props.className"
              :column-index="props.columnIndex"
              :columns-count="props.columnsCount"
              :row-type="props.rowType"
              :level="props.level"
              :expanded="props.expanded"
              :editor="props.editor"
              :type="props.type"
              :cur-edit-field="editField"
              :edit-type="kendo.editable"
              :edit-field="inEditField"
              :edit-detail-field="inEditDetailField"
              :extraparams="getExtra(props)"
              v-on="listeners"
              @register-template="registerTemplateRef"
              @unregister-template="unregisterTemplateRef"
            ></NumberTemplate>
          </template>
          <template v-slot:checkboxTemplate="{ props, listeners }">
            <CheckboxTemplate
              :table-id="_uid"
              :field="props.field"
              :data-item="props.dataItem"
              :format="props.format"
              :class-name="props.className"
              :column-index="props.columnIndex"
              :columns-count="props.columnsCount"
              :row-type="props.rowType"
              :level="props.level"
              :expanded="props.expanded"
              :editor="props.editor"
              :type="props.type"
              :cur-edit-field="editField"
              :edit-type="kendo.editable"
              :edit-field="inEditField"
              :edit-detail-field="inEditDetailField"
              :extraparams="getExtra(props)"
              v-on="listeners"
              @register-template="registerTemplateRef"
              @unregister-template="unregisterTemplateRef"
            ></CheckboxTemplate>
          </template>
          <template v-slot:tagsTemplate="{ props, listeners }">
            <TagsTemplate
              :table-id="_uid"
              :field="props.field"
              :data-item="props.dataItem"
              :format="props.format"
              :class-name="props.className"
              :column-index="props.columnIndex"
              :columns-count="props.columnsCount"
              :row-type="props.rowType"
              :level="props.level"
              :expanded="props.expanded"
              :editor="props.editor"
              :type="props.type"
              :cur-edit-field="editField"
              :edit-type="kendo.editable"
              :edit-field="inEditField"
              :edit-detail-field="inEditDetailField"
              :extraparams="getExtra(props)"
              v-on="listeners"
              @register-template="registerTemplateRef"
              @unregister-template="unregisterTemplateRef"
            ></TagsTemplate>
          </template>
          <template v-slot:groupsTemplate="{ props, listeners }">
            <GroupsTemplate
              :table-id="_uid"
              :field="props.field"
              :data-item="props.dataItem"
              :format="props.format"
              :class-name="props.className"
              :column-index="props.columnIndex"
              :columns-count="props.columnsCount"
              :row-type="props.rowType"
              :level="props.level"
              :expanded="props.expanded"
              :editor="props.editor"
              :type="props.type"
              :cur-edit-field="editField"
              :edit-type="kendo.editable"
              :edit-field="inEditField"
              :edit-detail-field="inEditDetailField"
              :extraparams="getExtra(props)"
              v-on="listeners"
              @register-template="registerTemplateRef"
              @unregister-template="unregisterTemplateRef"
            ></GroupsTemplate>
          </template>
          <template v-slot:enumTemplate="{ props, listeners }">
            <EnumTemplate
              :table-id="_uid"
              :field="props.field"
              :data-item="props.dataItem"
              :format="props.format"
              :class-name="props.className"
              :column-index="props.columnIndex"
              :columns-count="props.columnsCount"
              :row-type="props.rowType"
              :level="props.level"
              :expanded="props.expanded"
              :editor="props.editor"
              :type="props.type"
              :cur-edit-field="editField"
              :edit-type="kendo.editable"
              :edit-field="inEditField"
              :edit-detail-field="inEditDetailField"
              :extraparams="getExtra(props)"
              v-on="listeners"
              @register-template="registerTemplateRef"
              @unregister-template="unregisterTemplateRef"
            ></EnumTemplate>
          </template>
          <template v-slot:myTemplate="{ props, listeners }">
            <ColumnMenu
            :column="props.column"
            :filterable="props.filterable"
            :filter="props.filter"
            :sortable="props.sortable"
            :sort="props.sort"
            :columns="menuColumns"
            @columnssubmit="columnssubmit"
            v-on="listeners" />
          </template>
          <template v-slot:myTemplateNoCol="{ props, listeners }">
            <ColumnMenu
            :column="props.column"
            :filterable="props.filterable"
            :filter="props.filter"
            :sortable="props.sortable"
            :sort="props.sort"
            @sortchange="sortchange"
            v-on="listeners" />
          </template>
          <template v-slot:detailTemplate="{ props, listeners }">
            <DetailTemplate
              :table-id="_uid"
              :data-item="props.dataItem"
              :data-index="props.dataIndex"
              :selected-field="selectedField"
              :expand-field="expandField"
              :edit-field="inEditField"
              :edit-detail-field="inEditDetailField"
              @change="itemUpdate"
              v-on="listeners">
              <template v-if="$scopedSlots.kendorowdetail || $slots.kendorowdetail" v-slot:default="detailData">
                <slot name="kendorowdetail" v-bind="detailData" :table-id="_uid" />
              </template>
            </DetailTemplate>
          </template>
          <template v-slot:headerTemplate="{ props, listeners }">
            <HeaderTemplate
              :table-id="_uid"
              :field="props.field"
              :title="props.title"
              :sortable="props.sortable"
              :selection-value="props.selectionValue"
              :render="props.render"
              :sort="sort"
              :single="single"
              v-on="listeners">
            </HeaderTemplate>
          </template>
          <!-- <template v-slot:basic="{ props, listeners }">
            <CellComponent
              :field="props.field"
              :expanded="props.expanded"
              :row-type="props.rowType"
              :level="props.level"
              :column-index="props.columnIndex"
              :columns-count="props.columnsCount"
              :data-item="props.dataItem"
              :class-name="props.className"
              v-on="listeners"
            ></CellComponent>
          </template> -->
          <grid-norecords>
            {{ noRecords }}
          </grid-norecords>
        </Grid>
        <div v-if="hasFooter" class="k-grid-footer" style="padding: 0px 8px 0px 0px;">
          <div ref="footerwrap" class="k-grid-footer-wrap">
            <table ref="footertable">
              <colgroup ref="colgroup" />
              <tfoot>
                <slot name="kendofooter" :max-columns="visibleColumnsCount" />
              </tfoot>
            </table>
          </div>
        </div>
      </intl>
    </localization>
  </div>
</template>

<script>
import { i18n } from '@/i18n'
import { Grid, GridNoRecords } from '@progress/kendo-vue-grid'
import ColumnMenu from '@/components/kendo/ColumnMenu.vue'
import { IntlProvider, LocalizationProvider, loadMessages } from '@progress/kendo-vue-intl'
import gridKendo from '@/config/grid-kendo.js'
import Loading from 'vue-loading-overlay'
import ActionCell from '@/components/kendo/templates/ActionCell'
import CommonTemplate from '@/components/kendo/templates/CommonTemplate.vue'
import SelectionTemplate from '@/components/kendo/templates/SelectionTemplate.vue'
import ExpandTemplate from '@/components/kendo/templates/ExpandTemplate.vue'
import CheckboxTemplate from '@/components/kendo/templates/CheckboxTemplate.vue'
import FechaTemplate from '@/components/kendo/templates/FechaTemplate.vue'
import NumberTemplate from '@/components/kendo/templates/NumberTemplate.vue'
import TagsTemplate from '@/components/kendo/templates/TagsTemplate.vue'
import GroupsTemplate from '@/components/kendo/templates/GroupsTemplate.vue'
import EnumTemplate from '@/components/kendo/templates/EnumTemplate.vue'
import ActionsTemplate from '@/components/kendo/templates/ActionsTemplate.vue'
import DetailTemplate from '@/components/kendo/templates/DetailTemplate.vue'
import HeaderTemplate from '@/components/kendo/templates/HeaderTemplate.vue'
// import CellComponent from '@/components/kendo/CellComponent.vue'

import { process } from '@progress/kendo-data-query'
import hasNestedValue from '@/services/functions/hasNestedValue'
import getNestedValue from '@/services/functions/getNestedValue'
import setNestedValue from '@/services/functions/setNestedValue'
import getValueKeyOrDefault from '@/services/functions/getValueKeyOrDefault'

import ValueType from '@/classes/kendo/ValueType'
import NULL from '@/constants/NULL'

import { TIPOS_ONLY, TIPOS_EXCLUDE } from '@/config/tipos-only'
import { cloneDeep, isNil, uniqBy } from 'lodash'

Object.entries(gridKendo).forEach(([locale, messages]) => loadMessages(messages, locale))

const keyValidator = function (value) {
  const validate = v => typeof v === 'string' || typeof v === 'number'
  return Array.isArray(value) ? value.every(validate) : validate(value)
}

export default {
  name: 'kendo-grid-component',
  components: {
    Grid: Grid,
    'grid-norecords': GridNoRecords,
    ColumnMenu,
    localization: LocalizationProvider,
    intl: IntlProvider,
    Loading,
    CommonTemplate,
    SelectionTemplate,
    ExpandTemplate,
    ActionCell,
    CheckboxTemplate,
    FechaTemplate,
    NumberTemplate,
    TagsTemplate,
    GroupsTemplate,
    EnumTemplate,
    ActionsTemplate,
    DetailTemplate,
    HeaderTemplate,
  },
  props: {
    idVista: Number,
    value: {
      required: true
    },
    valueType: {
      type: String,
      default: ValueType.KEY,
      validator: value => Object.values(ValueType).includes(value)
    },
    valueKey: {
      type: [Array, String, Number],
      required: true,
      validator: keyValidator
    },
    includeKey: {
      type: [Array, String, Number],
      validator: keyValidator
    },
    data: {
      type: Array,
      default: () => []
    },
    kendo: {},
    selectedField: {
      type: String,
      default: 'selected'
    },
    inEditField: {
      type: String,
      default: 'inEdit'
    },
    inEditDetailField: {
      type: String,
      default: 'inEditDetail'
    },
    expandField: {
      type: String,
      default: 'expanded'
    },
    loading: {
      type: Boolean,
      default: false
    },
    single: {
      type: Boolean,
      default: false
    },
    selectOnRowClick: {
      type: Boolean,
      default: false
    },
    editOnRowClick: {
      type: Boolean,
      default: false
    },
    expandOnRowClick: {
      type: Boolean,
      default: false
    },
    isRowEditable: {
      type: Function,
      default: dataItem => true
    },
    updateColumns: {
      type: Boolean,
      default: true
    }
  },
  data: function () {
    return {
      noRecords: i18n.t('app.not_avaliable_data'),
      tiposOnly: TIPOS_ONLY,
      tiposExclude: TIPOS_EXCLUDE,
      addedColumnClass: 'added-column',
      selected: [],
      editions: [],
      editId: null,
      editField: null,
      group: this.kendo.group || [],
      sort: this.kendo.sort || [],
      templateRefs: new Map()
    }
  },
  computed: {
    intlClass: function () {
      return { 'k-grid--footer k-widget k-grid': this.hasFooter, 'k-grid-expand': this.isExpandable } 
    },
    gridClass: function () {
      return [`k-grid-${this._uid}`, { 'k-grid__inner': this.hasFooter }] 
    },
    darkMode: function () {
      return this.$store.getters['app/darkMode']
    },
    menuColumns: function () {
      return this.columns.filter(c => c.title)
    },
    noMenuColumns: function () {
      return this.columns.filter(c => !c.title)
    },
    groupable: function () {
      if (Object.prototype.hasOwnProperty.call(this.kendo, 'groupable')) {
        return this.kendo.groupable
      }
      return true
    },
    isLower1024: function () {
      return this.$store.getters['app/getIsLower1024']
    },
    numDynamicColumns: function () {
      return 4
    },
    widthStaticColumns: function () {
      return 100
    },
    columns: function () {
      return this.kendo.columns.filter(x => this.filterByOnly(x.only) && this.filterByExclude(x.exclude))
    },
    dataItems: function () {
      var dataItems = this.kendo?.dataResult?.data ?? []
      this.kendo.group.forEach(_ => { dataItems = dataItems.flatMap(x => x.items) })
      return dataItems
    },
    hasFooter: function () {
      return !!this.$slots.kendofooter || !!this.$scopedSlots.kendofooter
    },
    visibleColumnsCount: function () {
      if (!this.hasFooter) {
        return ''
      }
      return this.columns.filter(x => !x.hidden).length
    },
    hasSelected () {
      return this.selected.length > 0
    },
    areAllSelected () {
      return this.data.length === this.selected.length
    },
    isEditable: function () {
      return !!this.kendo.editable
    },
    isCellEditable: function () {
      return this.kendo.editable === 'cell'
    },
    isNavigatable: function () {
      return this.kendo.navigate === true
    },
    isExpandable: function () {
      return !!this.kendo.expandable
    },
    isManualExpandable: function () {
      return this.isExpandable && this.kendo.expandable === 'manual'
    },
    isAutoExpandable: function () {
      return this.isExpandable && !this.isManualExpandable
    },
    isSelectable: function () {
      return !!this.kendo.selectable
    },
    keepSelection: function () {
      return this.isSelectable && !!this.kendo.keepSelection
    },
    keepEditions: function () {
      return this.isEditable && !!this.kendo.keepEditions
    },
    detailTemplate: function () {
      if (this.isExpandable) {
        return 'detailTemplate'
      }
      return undefined
    },
    numGroups () {
      return this.kendo.group?.length || 0
    },
    numExpandedGroupLevels () {
      const countedFields = new Set()
      let count = 0

      const traverse = (items, level = 0) => {
        if (!Array.isArray(items) || level >= this.numGroups) return

        for (const item of items) {
          const isExpanded = !hasNestedValue(this.expandField, item) || getNestedValue(this.expandField, item) === true
          const key = `${item.field}.${level}`
          if (!isExpanded) continue

          if (!countedFields.has(key)) {
            countedFields.add(key)
            count++
          }

          if (Array.isArray(item.items)) {
            traverse(item.items, level + 1)
          }
        }
      }

      traverse(this.kendo.dataResult?.data || [])
      return count
    },
    isArrayKey: function () {
      return Array.isArray(this.valueKey)
    },
    isArrayIKey: function () {
      return Array.isArray(this.includeKey)
    },
    hasIncludeKey: function () {
      return !(isNil(this.includeKey) || (this.isArrayIKey && this.includeKey.length === 0))
    },
    modelValue: {
      get () {
        var value = this.value
        if (!Array.isArray(value)) {
          value = value ? [value] : []
        }
        if (this.single) {
          value = value.slice(0, 1)
        }
        return value
      },
      set (newSelection) {
        if (!Array.isArray(newSelection)) {
          newSelection = newSelection ? [newSelection] : []
        }
        if (this.valueType === ValueType.KEY) {
          newSelection = newSelection.map(dataItem => this.getValueKeyOrDefault(dataItem, { includeKey: this.includeKey, asObject: true }))
        }
        if (this.single) {
          newSelection = newSelection[0] || ''
        }
        this.$emit('input', newSelection)
      }
    },
  },
  watch: {
    data (value, oldValue) {
      this.updateData(value)
    },
    numExpandedGroupLevels (value, oldValue) {
      if (this.isExpandable && value !== oldValue) {
        this.removeKendoExpand(oldValue)
      }
    },
    modelValue: function (value, oldValue) {
      const pageSelection = []
      const valueKeys = new Set(value.map(x => this.getValueKeyOrDefault(x, { needNValue: this.valueType === ValueType.DATA_ITEM || this.hasIncludeKey })))
      this.dataItems.forEach(dataItem => {
        const valueKey = this.getValueKeyOrDefault(dataItem)
        const isSelected = valueKeys.has(valueKey)
        setNestedValue(this.selectedField, dataItem, isSelected, true)
        if (isSelected) pageSelection.push(valueKey)
      })
      this.selected = pageSelection
      this.updateAllSelectedHeader()
    }
  },
  mounted: function () {
    if (this.data?.length && this.kendo.dataResult === null) {
      this.updateData(this.data)
    }
    this.grid = this.$refs.grid
    this.$root.$on('kendo-grid-componente-handle-resize', () => {
      this.handleResize()
      this.updateFooter()
    })
    if (this.isExpandable) {
      this.removeKendoExpand()
    }
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
    this.updateFooter()
  },
  created: function () {
    if (this.isSelectable === true) {
      const index = this.kendo.columns.findIndex((x) => x.field === this.selectedField && x.cell === 'selectionTemplate')
      if (index === -1) {
        const column = { field: this.selectedField, title: '', cell: 'selectionTemplate', width: '50px', className: this.addedColumnClass, headerSelectionValue: false, filterable: false, locked: true, lockable: false, columnMenu: false, reorderable: false, groupable: false, sortable: false, editable: false, resizable: false }
        this.kendo.columns.unshift(column)
      } else if (!this.kendo.columns[index].className.includes(this.addedColumnClass)) {
        this.kendo.columns[index].className = `${this.kendo.columns[index].className} ${this.addedColumnClass}`
      } 
    }
    if (this.isNavigatable) {
      const index = this.kendo.columns.findIndex((x) => x.cell === 'myButtomTemplate')
      if (index === -1) {
        const column = { field: ' ', title: '', cell: 'myButtomTemplate', width: '125px', className: this.addedColumnClass, filterable: false, locked: true, lockable: false, columnMenu: false, reorderable: false, groupable: false, sortable: false, editable: false, resizable: false }
        if (this.kendo.columns[0].field === this.selectedField) this.kendo.columns.splice(1, 0, column)
        else this.kendo.columns.unshift(column)
      } else if (!this.kendo.columns[index].className.includes(this.addedColumnClass)) {
        this.kendo.columns[index].className = `${this.kendo.columns[index].className} ${this.addedColumnClass}`
      } 
    }
    if (this.isAutoExpandable) {
      const index = this.kendo.columns.findIndex((x) => x.field === this.expandField && x.cell === 'expandTemplate')
      if (index === -1) {
        const column = { field: this.expandField, title: '', cell: 'expandTemplate', width: '32px', className: `${this.addedColumnClass} !px-0`, filterable: false, locked: true, lockable: false, columnMenu: false, reorderable: false, groupable: false, sortable: false, editable: false, resizable: false }
        if (this.kendo.columns[0].field === this.selectedField) this.kendo.columns.splice(1, 0, column)
        else this.kendo.columns.unshift(column)
      } else if (!this.kendo.columns[index].className.includes(this.addedColumnClass)) {
        this.kendo.columns[index].className = `${this.kendo.columns[index].className} ${this.addedColumnClass}`
      }
    }
  },
  methods: {
    updateData: function (data) {
      const dataState = {
        skip: this.kendo.skip,
        take: this.kendo.take,
        sort: this.kendo.sort,
        group: this.kendo.group
      }
      var dataItems = cloneDeep(data) || []
      const localGroups = dataState.group || []
      if (dataItems.length) {
        dataItems.forEach(x => {
          const valueKey = this.getValueKeyOrDefault(x)
          if (localGroups.length) {
            localGroups.forEach(group => {
              if (!hasNestedValue(group.field, x, { checkNull: true, checkUndefined: true })) {
                setNestedValue(group.field, x, NULL)
              }
            })
          }
          if (this.isEditable) {
            setNestedValue(this.inEditField, x, valueKey === this.editId, true)
            setNestedValue(this.inEditDetailField, x, getNestedValue(this.inEditDetailField, x) || false, true)
            if (this.keepEditions && this.editions?.length) {
              const edition = this.editions.find(e => valueKey === this.getValueKeyOrDefault(e))
              if (edition) {
                const keys = Object.keys(edition).filter(key => ![this.valueKey].flat().includes(key))
                keys.forEach(key => setNestedValue(key, x, edition[key].value, true))
              }
            }
          }
          if (this.isSelectable && getNestedValue(this.selectedField, x) === undefined) {
            setNestedValue(this.selectedField, x, false, true)
          }
          if (this.isExpandable && getNestedValue(this.expandField, x) === undefined) {
            setNestedValue(this.expandField, x, false, true)
          }
        })
      }
      this.kendo.dataResult = process(dataItems, dataState)
      const numSelected = this.selected.length
      var mapping = false
      if (this.modelValue.length && this.keepSelection) {
        var valueKeys = this.modelValue
        if (this.valueType === ValueType.DATA_ITEM || this.isArrayKey || this.hasIncludeKey) {
          valueKeys = this.modelValue.map(dataItem => this.getValueKeyOrDefault(dataItem))
        }
        mapping = dataItem => !!valueKeys.some(valueKey => valueKey === this.getValueKeyOrDefault(dataItem))
      }
      if (!this.keepEditions) {
        this.editions = []
        if (this.modelValue.length && (this.valueType === ValueType.DATA_ITEM || this.hasIncludeKey)) {
          this.modelValue.forEach(v => {
            const valueKey = this.getValueKeyOrDefault(v)
            const dataItem = dataItems.find(x => valueKey === this.getValueKeyOrDefault(x))
            if (dataItem) {
              const keys = Object.keys(dataItem).filter(key => ![this.valueKey].flat().includes(key) && (this.valueType === ValueType.DATA_ITEM || [this.valueIKey].flat().includes(key)))
              keys.forEach(key => setNestedValue(key, v, getNestedValue(key, dataItem), true))
            }
          })
        }
      }
      this.mapSelectionKendoGrouping(mapping)
      this.updateAllSelectedHeader()
      if (numSelected !== this.selected.length) {
        this.$emit('headerselectionchange', { dataItems, value: this.areAllSelected, event: null })
      }
      // this.removeKendoExpand()
    },
    createAppState: function (dataState) {
      this.kendo.take = dataState.take
      this.kendo.skip = dataState.skip
      if (dataState.group) {
        // eslint-disable-next-line no-return-assign
        // dataState.group.map(group => group.aggregates = this.aggregates)
      }
      this.kendo.group = dataState.group
      this.kendo.filter = dataState.filter
      this.kendo.sort = dataState.sort
      this.group = this.kendo.group
      this.sort = this.kendo.sort      
    },
    datastatechange: async function (event) {
      this.createAppState(event.data)
      this.$emit('datastatechange', event)
    },
    columnreorder: function (event) {
      this.updateFooter()
      const updatedColumns = [...event.columns]
      this.insertHiddenColumns(event)
      this.reorderByProtectedColumns(event)
      if (this.updateColumns) {
        this.sendUpdatedColumns(updatedColumns)
      }
      this.kendo.columns = event.columns
      this.$emit('columnreorder', event)
    },
    insertHiddenColumns: function (event) {
      const splicedIndexes = []
      this.kendo.columns.forEach((column, index) => {
        if (!event.columns.some(x => x.field === column.field)) {
          event.columns.splice(index, 0, column)
          splicedIndexes.push(index)
        }
      })
      if (splicedIndexes.length) {
        event.columns.forEach((column, index) => {
          if (typeof column.orderIndex !== 'number') {
            this.$set(column, 'orderIndex', index)
          } else if (splicedIndexes.length && !column.hidden) {
            const nSpliced = splicedIndexes.filter(splicedIndex => splicedIndex <= column.orderIndex).length
            if (nSpliced > 0) {
              this.$set(column, 'orderIndex', column.orderIndex + nSpliced)
            }
          }
        })
      }
    },
    reorderByProtectedColumns: function (event) {
      const numProtected = (this.isSelectable) + (this.isAutoExpandable) + (this.isNavigatable)
      const orderedCols = [...event.columns].sort((a, b) => a.orderIndex - b.orderIndex)
      let fixCount = 0
      for (let i = 0; i < numProtected; i++) {
        const column = orderedCols[i]
        if (!column.className?.includes(this.addedColumnClass)) {
          this.$set(column, 'orderIndex', numProtected)
          fixCount++
        } else if (fixCount > 0) {
          this.$set(column, 'orderIndex', column.orderIndex - fixCount)
        }
      }
    },
    expandchange: function (event) {
      const field = event.target?.$props.expandField || event.field || this.expandField
      setNestedValue(field, event.dataItem, event.value, true)
      this.$emit('expandchange', event)
    },
    itemChange: function (event) {
      const oldValue = getNestedValue(event.field, event.dataItem)
      const changes = { [event.field]: { value: event.value, oldValue } }
      setNestedValue(event.field, event.dataItem, event.value, true)
      this.pushEdition(event.dataItem, changes)
      this.$emit('itemchange', { ...event, oldValue, fields: [event.field], changes })
    },
    itemUpdate: function (event) {
      this.pushEdition(event.dataItem, event.changes)
      this.$emit('itemchange', event)
    },
    pushEdition: function (dataItem, changes) {
      const flatKey = [this.valueKey].flat()
      const o = Object
      if (!o) {
        
      }
      const updatableKeys = Object.keys(changes).filter(key => !flatKey.includes(key))
      const valueKey = this.getValueKeyOrDefault(dataItem)
      if (this.keepEditions) {
        const editions = this.editions
        const index = editions.findIndex(e => valueKey === this.getValueKeyOrDefault(e))
        let edition = editions[index]
        if (!edition) {
          edition = {}
          flatKey.forEach(key => setNestedValue(key, edition, getNestedValue(key, dataItem), true))
          updatableKeys.forEach(key => this.$set(edition, key, changes[key]))
          editions.push(edition)
        } else {
          updatableKeys.forEach(key => {
            if (!edition[key]) {
              this.$set(edition, key, changes[key])
            } else if (edition[key].oldValue === changes[key].value) {
              delete edition[key]
            } else {
              this.$set(edition, `${key}.value`, changes[key].value)
            }
          })
          if (!Object.keys(edition).filter(key => !flatKey.includes(key)).length) {
            editions.splice(index, 1)
          }
        }
        this.editions = editions
      }
      if (this.modelValue.length) {
        const flatIKey = [this.includeKey].flat()
        const valueItem = this.valueType === ValueType.DATA_ITEM || this.hasIncludeKey ? this.value.find(x => this.getValueKeyOrDefault(x) === valueKey) : null
        if (valueItem) {
          updatableKeys.filter(key => this.valueType === ValueType.DATA_ITEM || flatIKey.includes(key))
            .forEach(key => setNestedValue(key, valueItem, changes[key].value, true))
        }
      }
    },
    exitcuredit: function (event) {
      const curDataItem = this.dataItems.find(dataItem => this.getValueKeyOrDefault(dataItem) === this.editId)
      if (curDataItem) {
        this.exitedit(curDataItem, true)
      }
    },
    exitedit: function (dataItem, exitEdit) {
      if (this.isEditable) {
        if (!exitEdit && getNestedValue(this.inEditField, dataItem)) {
          return
        }
        this.editId = null
        this.editField = null
        this.setInEdit(dataItem, false)
      }
    },
    rowclick: function (event) {
      if (this.selectOnRowClick) {
        this.selectionchange({ event, dataItem: event.dataItem })
      }
      this.$emit('rowclick', event)
    },
    cellclick: function (event) {
      if (this.isEditable && this.editOnRowClick) {
        const eventColumn = this.columns.find(col => col.field === event.field)
        const isEventCellEditable = eventColumn && (eventColumn?.editable ?? true)
        const valueKey = this.getValueKeyOrDefault(event.dataItem)
        const isKeyField = this.isArrayKey ? this.valueKey.includes(event.field) : this.valueKey === event.field
        this.dataItems.forEach((dataItem) => {
          if (getNestedValue(this.inEditField, dataItem) && (this.getValueKeyOrDefault(dataItem) !== valueKey)) {
            setNestedValue(this.inEditField, dataItem, false, true)
          }
        })
        if (this.isRowEditable(event.dataItem) && (!this.isCellEditable || isEventCellEditable)) {
          if (getNestedValue(this.inEditField, event.dataItem)) {
            if ((event.field === this.editField)) {
              return
            }
            if (valueKey === this.editId) {
              if (isKeyField) {
                this.exitedit(event.dataItem, true)
                return
              }
              this.editField = event.field
              return
            }
          }
    
          this.exitedit(event.dataItem, true)
          if (!isKeyField) {
            this.editField = event.field
          }
          this.setInEdit(event.dataItem, true)
        } else {
          this.exitedit(event.dataItem, true)
        }
      }
      this.$emit('cellclick', event)
    },
    setInEdit: function (dataItem, toEdit) {
      if (this.isEditable) {
        if (this.isRowEditable(dataItem)) {
          const curEdit = getNestedValue(this.inEditField, dataItem) 
          if (curEdit !== toEdit) {
            const editId = this.getValueKeyOrDefault(dataItem)
            toEdit = toEdit && editId !== this.editId
            this.editId = toEdit ? editId : null
            setNestedValue(this.inEditField, dataItem, toEdit, true)
            this.setBinding(toEdit)
          }
        } else {
          setNestedValue(this.inEditField, dataItem, false, true)
          this.setBinding(false)
        }
      }
    },
    setBinding: function (bind) {
      const kendoBody = this.$refs.grid?.$refs?.scrollContainer?.firstElementChild
      const binding = { arg: 'mousedown', value: { handler: this.exitcuredit } }
      const outside = this.$options.directives.outside
      if (kendoBody && outside) {
        if (bind) {
          outside.bind(kendoBody, binding, this._vnode)
        } else {
          outside.unbind(kendoBody)
        }
      }
    },
    columnssubmit: function (event) {
      const columns = this.noMenuColumns.concat(event)
      const options = { columns }
      this.columnreorder(options)
      // this.kendo.columns = columnsState
      this.$emit('columnssubmit', columns)
    },
    sortchange: function (event) {
      let sort = event.sort
      if (Array.isArray(event)) {
        sort = event
      }
      const dataState = {
        skip: this.kendo.skip,
        take: this.kendo.take,
        sort: sort,
        group: this.kendo.group,
        filter: this.kendo.filter
      }
      this.datastatechange({ data: dataState })
      this.$emit('sortchange', event)
    },
    filterchange: function (event) {
      const dataState = {
        skip: this.kendo.skip,
        take: this.kendo.take,
        sort: this.kendo.sort,
        group: this.kendo.group,
        filter: event
      }

      this.datastatechange({ data: dataState })
      this.$emit('filterchange', event)
    },
    selectionchange: function (event) {
      if (this.isSelectable) {
        const dataItems = event.dataItems || this.dataItems
        const isSelected = !getNestedValue(this.selectedField, event.dataItem)
        if (this.single) {
          dataItems.forEach(dataItem => setNestedValue(this.selectedField, dataItem, false, true))
        }
        setNestedValue(this.selectedField, event.dataItem, isSelected, true)
        this.updateModelValue([event.dataItem], isSelected)
        this.updateAllSelectedHeader()
        this.$emit('selectionchange', { dataItem: event.dataItem, isSelected, event, dataItems })
      }
    },
    headerselectionchange: function (event) {
      if (this.isSelectable && !this.single) {
        const checked = event.event.target.checked
        this.mapSelectionKendoGrouping(checked)
        this.updateAllSelectedHeader()
        this.$emit('headerselectionchange', { dataItems: this.dataItems, value: checked, event })
      }
    },
    updateAllSelectedHeader: function () {
      if (this.isSelectable) {
        if (!this.hasSelected) {
          this.kendo.columns[0].headerSelectionValue = false
        } else {
          this.kendo.columns[0].headerSelectionValue = this.areAllSelected
        }
      }
    },
    actionClicked: function (event) {
      this.$emit('actionclick', event)
    },
    mapSelectionKendoGrouping (checked) {
      this.updateModelValueByFunction(this.dataItems, checked)
    },
    updateModelValue: function (selected = [], toSelect = true) {
      var newSelection = this.modelValue
      const pageSelection = []
      if (this.single) {
        newSelection = toSelect ? selected.slice(0, 1) : []
        if (toSelect) {
          pageSelection.push(this.getValueKeyOrDefault(selected[0]))
        }
      } else {
        const valueKeys = selected.map(dataItem => this.getValueKeyOrDefault(dataItem))
        if (toSelect) {
          pageSelection.push(...valueKeys)
          newSelection = uniqBy([...this.modelValue, ...selected], dataItem => this.getValueKeyOrDefault(dataItem, { needNValue: typeof dataItem === 'object' }))
        } else {
          newSelection = this.modelValue.filter(dataItem => !valueKeys.includes(this.getValueKeyOrDefault(dataItem, { needNValue: this.valueType === ValueType.DATA_ITEM || this.hasIncludeKey })))
        }
      }
      this.selected = pageSelection
      this.modelValue = newSelection
    },
    updateModelValueByFunction: function (selected = [], toSelectFunc = dataItem => true) {
      if (typeof toSelectFunc !== 'function') {
        this.updateModelValue(selected, !!toSelectFunc)
        return
      }
      const pageSelection = []
      var newSelection = this.modelValue
      if (this.single) {
        const firstSelected = selected.find(toSelectFunc)
        newSelection = firstSelected ? [firstSelected] : []
        if (firstSelected) {
          pageSelection.push(this.getValueKeyOrDefault(firstSelected))
        }
      } else {
        selected.forEach((sDataItem) => {
          const valueKey = this.getValueKeyOrDefault(sDataItem)
          if (toSelectFunc(sDataItem)) {
            pageSelection.push(valueKey)
            newSelection = uniqBy([...newSelection, sDataItem], dataItem => this.getValueKeyOrDefault(dataItem, { needNValue: typeof dataItem === 'object' }))
          } else {
            newSelection = newSelection.filter(dataItem => valueKey !== this.getValueKeyOrDefault(dataItem, { needNValue: this.valueType === ValueType.DATA_ITEM }))
          }
        })
      }
      this.selected = pageSelection
      this.modelValue = newSelection
    },
    getValueKeyOrDefault: function (dataItem, options) {
      return getValueKeyOrDefault(dataItem, this.valueKey, options)
    },
    handleResize: function () {
      // const self = this
      // setTimeout(() => {
      //   if (self.grid !== undefined) {
      //     const currentVisibleColumns = self.columns.filter((c) => !(c.minGridWidth > self.grid.offsetWidth))
      //     if (currentVisibleColumns.length !== self.columns.length) {
      //       self.kendo.columns = currentVisibleColumns
      //     }
      //     const mapedColumns = self.columns.map(column => {
      //       if (self.isLower1024) {
      //         if (column.minWidth !== undefined && column.minWidth !== null) {
      //           column.width = column.minWidth
      //         }
      //       } else {
      //         if (column.minWidth !== undefined && column.minWidth !== null) {
      //           const topWidth = document.querySelector('#grid > div.k-grouping-header.k-grouping-header-flex').clientWidth
      //           column.width = ((topWidth - self.widthStaticColumns) / self.numDynamicColumns) + 'px'
      //           // column.width = '100%'
      //           // delete column.width
      //         } 
      //       }
      //       return column
      //     })
      //     self.kendo.columns = mapedColumns
      //   }
      // }, 50)
      if (this.grid !== undefined) {
        const currentVisibleColumns = this.columns.filter((c) => !(c.minGridWidth > this.grid.offsetWidth))
        if (currentVisibleColumns.length !== this.columns.length) {
          this.kendo.columns = currentVisibleColumns
        }
      }
    },
    scroll: function (event) {
      if (this.hasFooter && this.$refs.footerwrap) {
        this.$refs.footerwrap.scrollLeft = event.target.scrollLeft
      }
    },
    columnResize: function (event) {
      const callbacks = this.getRefsCallbacks(event.index, 'columnResize')
      callbacks.forEach(callback => callback(event.newWidth))
      this.updateFooterStyle()
    },
    updateFooter: function () {
      if (this.hasFooter) {
        this.updateFooterColGroup()
        this.updateFooterStyle()
      }
    },
    removeKendoExpand: function (oldNumExpandedGroupLevels = this.numExpandedGroupLevels) {
      if (this.isExpandable) {
        const targetIndex = Math.min(this.numExpandedGroupLevels + 1, this.numGroups) + (this.numExpandedGroupLevels > oldNumExpandedGroupLevels)
        const bodyColGroup = this.$refs.grid?.$refs.colGroup
        const headerColGroup = this.$refs.grid.$el.querySelector('.k-grid-header-wrap colgroup')
        const remove = colGroup => {
          if (colGroup) {
            const cols = Array.from(colGroup.children)
            const zeroColIndex = cols.findIndex(col => col.width === '0px')
            if (zeroColIndex > -1) {
              if (zeroColIndex !== targetIndex) {
                const zeroCol = cols[zeroColIndex]
                if (targetIndex < cols.length) {
                  colGroup.insertBefore(zeroCol, cols[targetIndex])
                } else {
                  colGroup.appendChild(zeroCol)
                }
              }
            } else {
              this.$set(cols[Math.max(targetIndex - 1, 0)], 'width', '0px')
            }
          }
        }
        remove(bodyColGroup)
        remove(headerColGroup)
      }
    },
    updateFooterColGroup: function () {
      if (this.hasFooter && this.$refs.colgroup) {
        if (!this.$refs.grid || !this.$refs.grid.$refs.colGroup) {
          this.$refs.colgroup.innerHTML = this.columns.map(col => `<col key="${col.field || col.title}" ${col.hidden ? 'hidden' : ''} width="${col.width}" />`).join('')
        } else {
          const self = this
          try {
            this.$nextTick(() => {
              while (self.$refs.colgroup.childElementCount > 0) {
                self.$refs.colgroup.childNodes[0].remove()
              }
              self.$refs.grid.$refs.colGroup.childNodes.forEach(x => self.$refs.colgroup.appendChild(x.cloneNode(true)))
            })
          } catch (error) {
          }
        } 
      }
    },
    updateFooterStyle: function () {
      if (this.hasFooter && this.$refs.colgroup) {
        if (!this.$refs.grid || !this.$refs.grid.$refs.colGroup) {
          this.$refs.footertable.style.width = this.columns.filter(x => !x.hidden).map(x => x.width).join(' + ')
        } else {
          const self = this
          try {
            this.$nextTick(() => {
              const width = self.$refs.grid.$refs.colGroup.parentNode.style.width
              self.$refs.footertable.style.width = width
              self.$refs.grid.$refs.colGroup.childNodes.forEach((x, i) => self.$refs.colgroup.childNodes[i].setAttribute('width', x.getAttribute('width')))
            })
          } catch (error) {
          }
        } 
      }
    },
    getColumn: function (props) {
      var column
      var columns = this.columns.filter(col => !col.hidden)
      const index = props.columnIndex - this.isExpandable
      if (columns[index]?.field === props.field) {
        column = columns[index]
      } else if (props.rowType === 'groupHeader') {
        column = columns.find(x => props.dataItem.field === x.field)
      } else {
        column = columns.find(x => props.field === x.field)
      }
      return column
    },
    getExtra: function (props, column) {
      if (!props) {
        return undefined
      }
      if (!column) {
        column = this.getColumn(props)
      }
      
      return column ? { ...column.extraparams, title: column.title, field: column.field, editable: this.isEditable && column.editable !== false } : {}
    },
    filterByOnly: function (only = {}) {
      const empty = () => true
      return !Object.keys(only || {}).length || 
        Object.keys(only).every(key => (this.tiposOnly[key] || empty)({ items: only[key], store: this.$store, route: this.$route, data: this.dataResult?.data }))
    },
    filterByExclude: function (exclude) {
      const empty = () => true
      return !Object.keys(exclude || {}).length || 
        Object.keys(exclude).every(key => (this.tiposExclude[key] || empty)({ items: exclude[key], store: this.$store, route: this.$route, data: this.dataResult?.data }))
    },
    registerTemplateRef: function (_uid, columnIndex, events = []) {
      if (!this.templateRefs.has(_uid)) {
        events.forEach(event => {
          event.name = event.name || ''
          event.callbacks = Array.isArray(event.callbacks) ? event.callbacks.filter(callback => typeof callback === 'function') : []
        })
        this.templateRefs.set(_uid, { _uid, columnIndex, events: events })
      }
    },
    unregisterTemplateRef: function (_uid) {
      if (this.templateRefs.has(_uid)) {
        this.templateRefs.delete(_uid)
      }
    },
    getRefsCallbacks: function (columnIndex, event) {
      return [...this.templateRefs.values()].filter(x => x.columnIndex === columnIndex).flatMap(x => x.events.filter(e => e.name === '' || e.name === event).flatMap(e => e.callbacks))
    }
  },
  beforeDestroy () {
    if (this.kendo) {
      if (this.kendo.dataResult) {
        this.kendo.dataResult = null
      }
      if (this.kendo.columns?.[0]) {
        this.kendo.columns[0].headerSelectionValue = false
      }
    }
  },
}
</script>

<style lang="scss">
.kendo-wrapper-height-adjust {
  & > div:not(.vld-overlay) {
    height: 100%;
    & > div:not(.vld-overlay) {
      height: 100%;
    }
  }
}
.k-grid {
  height: 100%;
}
</style>
<style scoped>
.k-grid-expand .k-grid::v-deep .k-hierarchy-cell .k-column-resizer {
  display: none !important;
}

::v-deep .k-grid td{
  overflow: hidden;
  white-space: nowrap;
}

/* ::v-deep .tags-template, .groups-template{
   overflow: auto !important;
   white-space: nowrap !important;
 }
*/

</style>
